/**
 * Phone / browser Back for full-page sheets, the menu and the scanner: each open page adds one
 * history entry and Back closes the top one. A page closed from inside the app removes its own entry
 * with history.back(). That step is our bookkeeping, so the router never sees it (our listener runs
 * first and stops it); and if the app navigated while it was in flight, the address is put back to
 * where the user went. Pages opened meanwhile wait for it, so the two never cross.
 */
type Entry = { id: number; close(): void; pushed: boolean; gone: boolean };

const stack: Entry[] = [];
const waiting: Entry[] = [];
let seq = 0;
let pendingBacks = 0;
let navigatedMeanwhile: [unknown, string] | null = null;
let ours = false; // our own pushState calls

const nativePush = history.pushState.bind(history);
history.pushState = function (data: unknown, unused: string, url?: string | URL | null) {
  if (pendingBacks > 0 && !ours) navigatedMeanwhile = [data, String(url ?? location.href)];
  nativePush(data, unused, url);
};
const pushOurs = (data: unknown, url?: string) => { ours = true; try { nativePush(data, '', url); } finally { ours = false; } };

function push(e: Entry) {
  if (e.gone) return;
  pushOurs({ ...(history.state ?? {}), sdBack: e.id });
  e.pushed = true;
}

// Registered when this module loads, before the router mounts, so it runs first.
window.addEventListener('popstate', (ev) => {
  if (pendingBacks > 0) {
    ev.stopImmediatePropagation(); // our own step back: not a navigation
    if (--pendingBacks > 0) return;
    if (navigatedMeanwhile) { const [d, u] = navigatedMeanwhile; navigatedMeanwhile = null; pushOurs(d, u); }
    waiting.splice(0).forEach(push);
    return;
  }
  const top = stack.pop();
  if (top) { top.gone = true; top.close(); }
});

/** Register an open page; returns the function to call when it closes. */
export function onBack(close: () => void): () => void {
  const e: Entry = { id: ++seq, close, pushed: false, gone: false };
  stack.push(e);
  if (pendingBacks > 0) waiting.push(e); else push(e);
  return () => {
    if (e.gone) return; // already closed by Back
    e.gone = true;
    const i = stack.indexOf(e);
    if (i >= 0) stack.splice(i, 1);
    // Only step back over our own entry: if the app navigated first, leave history alone.
    if (e.pushed && history.state?.sdBack === e.id) { pendingBacks++; history.back(); }
  };
}
